import { DescriptionsItemType } from 'antd/es/descriptions';
import { Duration } from 'dayjs/plugin/duration';
import { ILanguage, i18n } from 'translations';
import { useTranslation } from 'translations/hooks';

import { SECONDARY_MAX_DECIMAL_DIGITS } from 'business/constants';
import { formatDurationToReadable } from 'business/shift-report/report/services/timeOperations';
import { GetIndicatorResultsQuery } from 'generated/graphql';
import { createNumberFormatter } from 'technical/format/number';
import { notEmpty } from 'technical/utils/not-empty';

type UseBuildTotalAvailabilityDescriptionItems = {
  totalTasksTime: Duration;
  totalCriticalTasksTime: Duration;
  indicators: NonNullable<
    GetIndicatorResultsQuery['calculateIndicatorResults']
  >['data'];
};

function displayValueAndUnit(
  value: unknown,
  distanceUnit: string,
  formatter: Intl.NumberFormat,
) {
  return Number.isFinite(value)
    ? `${formatter.format(value as number)}\u00A0${distanceUnit}`
    : 'N/A';
}

export const useBuildTotalAvailabilityDescriptionItems = ({
  totalTasksTime,
  totalCriticalTasksTime,
  indicators,
}: UseBuildTotalAvailabilityDescriptionItems) => {
  const { t, language } = useTranslation();

  const numberFormatter = createNumberFormatter(
    language,
    SECONDARY_MAX_DECIMAL_DIGITS,
  );

  let indicatorItems: DescriptionsItemType[] = [];
  if (indicators && indicators.length > 0) {
    indicatorItems = indicators
      .map((indicator, index) => {
        const indicatorSpan =
          indicators.length % 3 !== 0 &&
          index >= indicators.length - (indicators.length % 3)
            ? 3
            : 2;

        if (indicator) {
          return {
            key: indicator.id,
            label:
              {
                [ILanguage.fr]: indicator.nameFr,
                [ILanguage.enGB]: indicator.nameEn,
                [ILanguage.enUS]: indicator.nameEn,
                [ILanguage.es]: indicator.nameEs,
              }[i18n.language] ?? indicator.nameEn,
            children: displayValueAndUnit(
              indicator.value,
              indicator.unit,
              numberFormatter,
            ),
            className: 'item',
            span: indicatorSpan,
          };
        }
      })
      .filter(notEmpty) satisfies DescriptionsItemType[];
  }

  const firstRowSpan = 3;
  const firstRow = [
    {
      key: 'totalTime',
      label: t('pages.print.totalTime'),
      children: formatDurationToReadable(totalTasksTime.asMinutes()),
      className: 'item',
      span: firstRowSpan,
    },
    {
      key: 'totalCriticalTime',
      label: t('pages.print.totalCriticalTime'),
      children: formatDurationToReadable(totalCriticalTasksTime.asMinutes()),
      className: 'item',
      span: firstRowSpan,
    },
  ];

  const items = [
    ...firstRow,
    ...indicatorItems,
  ] satisfies DescriptionsItemType[];

  return items;
};
